import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { Car, fullName } from '../api/cars';
import { UseCarsResult } from './useCars';

const label = (t: string) => t.charAt(0) + t.slice(1).toLowerCase();

// Dates are stored as instants but only the day carries meaning here, so the
// day is read in UTC. Formatting in the viewer's zone would slide a car onto
// the day before for anyone west of Greenwich and break the grouping the
// calendar in the iOS app depends on.
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

// Only the rarest tiers get colour, so the badge means something at a glance
// instead of turning the whole column into decoration.
const typeBadgeColor = (type: Car['type']) => {
  switch (type) {
    case 'HYPER':
      return 'red' as const;
    case 'EXOTIC':
    case 'SUPER':
      return 'blue' as const;
    default:
      return 'grey' as const;
  }
};

export const CarTable = ({ cars, loading, error, configured, reload }: UseCarsResult) => (
  <Table
    variant="borderless"
    renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
      `Displaying cars ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
    }
    columnDefinitions={[
      {
        id: 'car',
        header: 'Car',
        cell: (item: Car) => fullName(item),
        sortingField: 'manufacturer',
        isRowHeader: true,
      },
      {
        id: 'country',
        header: 'Country of origin',
        cell: (item: Car) => item.countryOfOrigin,
        sortingField: 'countryOfOrigin',
      },
      {
        id: 'type',
        header: 'Type',
        cell: (item: Car) => (
          <Badge color={typeBadgeColor(item.type)}>{label(item.type)}</Badge>
        ),
        sortingField: 'type',
      },
      {
        id: 'topSpeed',
        header: 'Top speed',
        cell: (item: Car) => `${item.topSpeed} mph`,
        sortingField: 'topSpeed',
      },
      {
        id: 'mainUse',
        header: 'Main use',
        cell: (item: Car) => label(item.mainUse),
        sortingField: 'mainUse',
      },
      {
        id: 'datePurchased',
        header: 'Purchased',
        cell: (item: Car) => day(item.datePurchased),
        sortingField: 'datePurchased',
      },
      {
        id: 'lastDriven',
        header: 'Last driven',
        cell: (item: Car) =>
          item.lastDriven ? (
            day(item.lastDriven)
          ) : (
            <Box color="text-status-inactive">Never</Box>
          ),
        sortingField: 'lastDriven',
      },
    ]}
    enableKeyboardNavigation
    items={cars}
    loading={loading}
    loadingText="Loading cars"
    trackBy="id"
    empty={
      <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
        <SpaceBetween size="m">
          <b>
            {error
              ? 'Could not load cars'
              : configured
              ? 'No cars yet'
              : 'GraphQL not configured'}
          </b>
          {error ? <Box variant="p">{error}</Box> : null}
        </SpaceBetween>
      </Box>
    }
    header={
      <Header
        counter={cars.length ? `(${cars.length})` : undefined}
        actions={
          <Button iconName="refresh" onClick={reload} disabled={loading}>
            Refresh
          </Button>
        }
      >
        Cars
      </Header>
    }
  />
);
